import styled from 'styled-components';
import { NavLink } from 'react-router-dom';

export const StyledNavigationContainer = styled.div<{ $isCollapsed: boolean }>`
  @media ${({ theme }) => theme.media.desktop} {
    position: fixed;
    top: 0;
    left: 0;
    width: ${({ $isCollapsed }) => $isCollapsed ? '80px' : '240px'};
    height: 100vh;
    z-index: ${({ theme }) => theme.zIndex.fixed || 1000};
    transition: width 0.3s ease;
  }
`;

export const StyledMobileHeader = styled.div`
  display: none;
  
  @media ${({ theme }) => theme.media.mobile} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${({ theme }) => theme.spacing.md};
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    position: sticky;
    top: 0;
    z-index: ${({ theme }) => theme.zIndex.sticky};
  }
`;

export const StyledMobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }

  @media ${({ theme }) => theme.media.touch} {
    width: 44px;
    height: 44px;
  }
`;

export const StyledNavigationOverlay = styled.div<{ $isOpen: boolean }>`
  @media ${({ theme }) => theme.media.mobile} {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: ${({ theme }) => theme.zIndex.modalBackdrop};
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    transition: ${({ theme }) => theme.transitions.default};
  }
`;

export const StyledNavigation = styled.nav<{ $isOpen: boolean; $isCollapsed: boolean }>`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing.md} 0;
  overflow-y: auto;
  height: 100%;
  position: relative;
  
  @media ${({ theme }) => theme.media.desktop} {
    display: block;
  }
  
  @media ${({ theme }) => theme.media.mobile} {
    position: fixed;
    top: 0;
    left: 0;
    width: 280px;
    max-width: 80vw;
    z-index: ${({ theme }) => theme.zIndex.offcanvas};
    transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
    transition: transform 0.3s ease-in-out;
    box-shadow: ${({ theme }) => theme.colors.cardShadowHover};
  }

  @media ${({ theme }) => theme.media.reducedMotion} {
    transition: none;
  }
`;

export const StyledMobileCloseButton = styled.button`
  display: none;
  
  @media ${({ theme }) => theme.media.mobile} {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.textPrimary};
    cursor: pointer;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    transition: ${({ theme }) => theme.transitions.default};
    margin: 0 ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.md} auto;

    &:hover {
      background: ${({ theme }) => theme.colors.backgroundTertiary};
    }
  }
`;

export const StyledLogo = styled.div<{ $isCollapsed: boolean }>`
  padding: 0 ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  @media ${({ theme }) => theme.media.desktop} {
    ${({ $isCollapsed }) => $isCollapsed && `
      padding-left: 20px;
      padding-right: 20px;
      text-align: center;
    `}
  }
  
  @media ${({ theme }) => theme.media.mobile} {
    display: none;
  }
`;

export const StyledLogoText = styled.h1<{ $isCollapsed: boolean }>`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin: 0;
  
  @media ${({ theme }) => theme.media.desktop} {
    ${({ $isCollapsed, theme }) => $isCollapsed && `
      font-size: ${theme.fontSizes.lg};
    `}
  }
`;

export const StyledMobileLogo = styled.div`
  display: none;
  
  @media ${({ theme }) => theme.media.mobile} {
    display: block;
    color: ${({ theme }) => theme.colors.primary};
    font-size: ${({ theme }) => theme.fontSizes.lg};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
  }
`;

export const StyledNavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const StyledNavItem = styled.li`
  margin: 0;
`;

export const StyledNavLink = styled(NavLink)<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  transition: ${({ theme }) => theme.transitions.default};
  min-height: 48px;
  position: relative;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
    color: ${({ theme }) => theme.colors.primary};
  }
  
  &.active {
    background: ${({ theme }) => theme.colors.primary}15;
    color: ${({ theme }) => theme.colors.primary};
    border-right: 3px solid ${({ theme }) => theme.colors.primary};
  }
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  @media ${({ theme }) => theme.media.desktop} {
    ${({ $isCollapsed }) => $isCollapsed && `
      justify-content: center;
      padding-left: 30px;
      padding-right: 30px;
      
      .nav-text {
        display: none;
      }
    `}
  }

  @media ${({ theme }) => theme.media.touch} {
    min-height: 44px;
  }
`;

export const StyledBottomSection = styled.div`
  margin-top: auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

export const StyledUserInfoSection = styled.div<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: ${({ theme }) => theme.transitions.default};

  @media ${({ theme }) => theme.media.desktop} {
    ${({ $isCollapsed }) => $isCollapsed && `
      justify-content: center;
      
      .user-details {
        display: none;
      }
    `}
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }
`;

export const StyledUserDetails = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

export const StyledUserName = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledUserEmail = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledSeparator = styled.hr`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing.md} 0;
`;

export const StyledLanguageSwitcherContainer = styled.div<{ $isCollapsed: boolean }>`
  padding: 0 ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  @media ${({ theme }) => theme.media.desktop} {
    ${({ $isCollapsed }) => $isCollapsed && `
      padding-left: 20px;
      padding-right: 20px;
    `}
  }
`;

export const StyledLogoutButton = styled.button<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  min-height: 48px;
  
  &:hover {
    background: ${({ theme }) => theme.colors.danger}10;
    color: ${({ theme }) => theme.colors.danger};
  }
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  @media ${({ theme }) => theme.media.desktop} {
    ${({ $isCollapsed }) => $isCollapsed && `
      justify-content: center;
      padding-left: 30px;
      padding-right: 30px;
      
      .logout-text {
        display: none;
      }
    `}
  }

  @media ${({ theme }) => theme.media.touch} {
    min-height: 44px;
  }
`;

export const StyledCollapseButton = styled.button`
  display: none;
  
  @media ${({ theme }) => theme.media.desktop} {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: ${({ theme }) => theme.spacing.lg};
    right: -12px;
    width: 24px;
    height: 24px;
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 50%;
    color: ${({ theme }) => theme.colors.textSecondary};
    cursor: pointer;
    transition: ${({ theme }) => theme.transitions.default};
    z-index: 10;

    &:hover {
      background: ${({ theme }) => theme.colors.primary}10;
      color: ${({ theme }) => theme.colors.primary};
      border-color: ${({ theme }) => theme.colors.primary};
    }
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;
